import { Alert, useToast } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import pluralize from "pluralize";

import { PermissionedButton } from "src/components/permission";
import {
  useSetWarehouseExportEnabledForSourceMutation,
  useWarehouseExportDisabledSyncCountQuery,
} from "src/graphql";

type Props = {
  sourceId: number;
  /** Whether this source type supports Warehouse Export at all. */
  supportsWarehouseExport: boolean;
};

/**
 * Bulk "re-enable Warehouse Export" for a source: when WHE was disabled on one
 * or more of the source's syncs after a failure, offers a single action to
 * clear those disables instead of visiting each sync. Only queries the count
 * when the source type supports WHE and the surfacing flag is on, so no work is
 * done where it would make no sense.
 */
export const SourceWarehouseExportReEnable = ({
  sourceId,
  supportsWarehouseExport,
}: Props) => {
  const { toast } = useToast();
  const flags = useFlags();
  const surfacingEnabled = Boolean(flags.wheFirstClassSurfacing);
  const active = supportsWarehouseExport && surfacingEnabled;

  const { data: count } = useWarehouseExportDisabledSyncCountQuery(
    { sourceId },
    {
      enabled: active,
      select: (data) => data.warehouseExportDisabledSyncCount,
    },
  );

  const { mutateAsync, isLoading } =
    useSetWarehouseExportEnabledForSourceMutation();

  if (!active || !count) {
    return null;
  }

  const reEnableAll = async () => {
    try {
      const result = await mutateAsync({ sourceId });
      const reEnabled = result.setWarehouseExportEnabledForSource.count;
      toast({
        id: "warehouse-export-reenabled-source",
        title: "Warehouse Export re-enabled",
        message: `Re-enabled Warehouse Export for ${pluralize("sync", reEnabled, true)}. It will be used again on their next run.`,
        variant: "success",
      });
    } catch (error) {
      toast({
        id: "warehouse-export-reenabled-source",
        title: "Couldn't re-enable Warehouse Export",
        message: error.message,
        variant: "error",
      });
    }
  };

  return (
    <Alert
      type="warning"
      variant="inline"
      title="Warehouse Export is disabled for some syncs"
      message={`Warehouse Export was disabled for ${pluralize("sync", count, true)} on this source after a failure. If you've resolved the issue, you can re-enable it for all of them at once.`}
      actions={
        <PermissionedButton
          size="sm"
          variant="secondary"
          permission={{
            v2: { resource: "source", grant: "can_update", id: sourceId },
          }}
          isLoading={isLoading}
          onClick={reEnableAll}
        >
          Re-enable all syncs
        </PermissionedButton>
      }
    />
  );
};
